/**
 * DriverArea — "do we know the customer well enough to print, and to collect?"
 *
 * PRINTED VALUES FIRST, RECORDS SECOND, and the order is the whole redesign of this area. The
 * screen this replaces led with "Personal Information" — a card containing the date of birth,
 * which prints on nothing and gates nothing — and buried the address, which is a required merge
 * field of the hire purchase agreement AND the one contact detail with a real wait, because most
 * drivers have to go and find their digital address before they can give it to you.
 *
 * THE GHANA CARD NUMBER IS A BLANK FILL-IN LINE ON THE INSTRUMENT, NOT A MERGE FIELD. No
 * ID-number column exists anywhere in the schema (`driver_identification` is id, timestamps,
 * driver_id, document_url, expires_at, type), so the two identity documents feed nothing and gate
 * nothing. They are records, and the file says so rather than wearing a red cross.
 *
 * FOUR BLOCKS, FOUR COMMITS, AND THAT IS THE BUG FIX. The two sheets this replaces held every
 * profile column in one `useState` bag and sent all eight on every save — so correcting a phone
 * number rewrote the name, the address and the date of birth from whatever the form was holding,
 * which after a background refetch is not necessarily what the server holds. Each block now sends
 * only the columns it owns; `saveDriverProfile` takes a patch for exactly that reason.
 *
 * THE STORAGE PATHS ARE UNTOUCHED. `profiles/photos/<uuid>.<ext>`,
 * `driver-identification/documents/licences/…` and `…/national-ids/…` are where every document
 * already on file lives; a tidier path is a file nobody can find.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';

import { driverProfileOf } from '@/lib/driverProfile';

import {
    contractDriverKey,
    fetchContractDriver,
    saveDriverDocument,
    saveDriverProfile,
    saveRepaymentTag,
} from '../data/driver';
import {
    documentStatus,
    driverFileCount,
    findDocument,
    DRIVER_DOCUMENT_TYPES,
    type DriverDocument,
} from '../data/driverDocs';
import { DRIVER_DOC_REFUSALS, fieldErrorsFrom } from '../data/refusalFields';
import InlineFieldGroup from '../inline/InlineFieldGroup';
import InlineUpload from '../inline/InlineUpload';
import QuietField from '../inline/QuietField';
import type { FieldValues } from '../inline/inlineEditReducer';
import { useInlineGroup } from '../inline/useInlineGroup';
import { useStagedFile } from '../inline/useStagedFile';
import { Block, Blocks, Hint, Rows } from './blocks';

interface Props {
    contractId: number;
    onSuccess: () => void;
}

/** `yyyy-MM-dd`, which is what a native date input holds and what a `date` column takes. */
function dateValue(value: string | null | undefined): string {
    return value ? format(new Date(value), 'yyyy-MM-dd') : '';
}

// ─── One identity document ───────────────────────────────────────────────────

/**
 * A document slot and its expiry, committed together.
 *
 * ITS OWN COMPONENT BECAUSE IT IS ITS OWN GROUP — two documents means two reducers, and hooks
 * cannot be called in a loop. It is also the honest unit: replacing a licence and correcting a
 * Ghana Card's expiry are two unrelated acts, and one commit bar over both would say otherwise.
 */
function DocumentGroup({
    type,
    label,
    documents,
    driverId,
    contractId,
    onSaved,
}: {
    type: string;
    label: string;
    documents: DriverDocument[];
    driverId: number;
    contractId: number;
    onSaved: () => void;
}) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);

    const existing = findDocument(documents, type);
    const status = documentStatus(existing);

    const server = useMemo<FieldValues>(
        () => ({ expiresAt: dateValue(existing?.expires_at), document: '' }),
        [existing?.expires_at],
    );

    const group = useInlineGroup({
        server,
        // THE SUBJECT IS THE SLOT. A driver has at most one of each type and replacing a document
        // updates that row in place, so nothing here is ever swapped — and keying it to the row id
        // would make the first upload look like a change of subject, resetting the group and taking
        // its `Saved …` line with it a second after it appeared.
        subject: `doc:${driverId}:${type}`,
        fieldErrorsFor: fieldErrorsFrom(DRIVER_DOC_REFUSALS),
        save: (draft) =>
            saveDriverDocument({
                driverId,
                type,
                existing,
                file: file.file,
                expiresAt: draft.expiresAt,
            }),
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: contractDriverKey(contractId) });
            onSaved();
        },
    });
    const file = useStagedFile(group, 'document');

    return (
        <InlineFieldGroup
            group={group}
            summary={{
                label,
                value: status.meta,
                open,
                onToggle: () => setOpen((wasOpen) => !wasOpen),
            }}
        >
            <Rows>
                <InlineUpload
                    id={`f-doc-${type}`}
                    label={label}
                    current={{ url: existing?.document_url ?? null }}
                    file={file.file}
                    onFileChange={file.stage}
                    accept="image/*,.pdf"
                    error={group.field('document').error}
                    disabled={group.saving}
                    bad={status.expired}
                    empty="Not on file"
                />
                <QuietField
                    {...group.field('expiresAt')}
                    id={`f-doc-${type}-expiry`}
                    label="Expires"
                    type="date"
                    format="leave empty where the card carries none"
                />
            </Rows>
        </InlineFieldGroup>
    );
}

// ─── The area ────────────────────────────────────────────────────────────────

export default function DriverArea({ contractId, onSuccess }: Props) {
    const queryClient = useQueryClient();

    /** The same key and the same select the driver surface has always used. */
    const { data: contractData, isLoading } = useQuery({
        queryKey: contractDriverKey(contractId),
        queryFn: () => fetchContractDriver(contractId),
        enabled: !!contractId,
    });

    const driver = contractData?.drivers;
    // THE PICK IS `admin_id IS NULL`, NOT `[0]`, AND ON THIS SURFACE IT IS A WRITE HAZARD RATHER
    // THAN A DISPLAY ONE. `profileId` below is the id every block's save targets, so a driver whose
    // embed also carries an admin's test profile could have had the admin's row filled in with the
    // driver's name, phone and date of birth — silently, one inline commit at a time.
    const profile = driverProfileOf(driver?.profiles);
    const documents = useMemo(
        () => (driver?.driver_identification ?? []) as DriverDocument[],
        [driver?.driver_identification],
    );

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: contractDriverKey(contractId) });
        // The manifest counts the driver file and prints the legal name, and both come off the
        // contract read rather than this one.
        queryClient.invalidateQueries({ queryKey: ['contract-setup'] });
        onSuccess();
    };

    const profileId = profile?.id ?? 0;
    const driverId = driver?.id ?? 0;

    const nameServer = useMemo<FieldValues>(
        () => ({
            firstName: profile?.first_name ?? '',
            middleName: profile?.middle_name ?? '',
            lastName: profile?.last_name ?? '',
        }),
        [profile?.first_name, profile?.middle_name, profile?.last_name],
    );

    const name = useInlineGroup({
        server: nameServer,
        subject: profileId ? `profile:${profileId}` : null,
        save: (draft) =>
            saveDriverProfile({
                profileId,
                patch: {
                    first_name: draft.firstName,
                    middle_name: draft.middleName || null,
                    last_name: draft.lastName,
                },
            }),
        onSaved: invalidate,
    });

    const contactServer = useMemo<FieldValues>(
        () => ({
            phone: profile?.phone ?? '',
            address: profile?.address ?? '',
            email: profile?.email ?? '',
        }),
        [profile?.phone, profile?.address, profile?.email],
    );

    const contact = useInlineGroup({
        server: contactServer,
        subject: profileId ? `profile:${profileId}` : null,
        save: (draft) =>
            saveDriverProfile({
                profileId,
                patch: {
                    phone: draft.phone,
                    address: draft.address || null,
                    email: draft.email || null,
                },
            }),
        onSaved: invalidate,
    });

    const personalServer = useMemo<FieldValues>(
        () => ({
            birthDate: dateValue(profile?.birth_date),
            repaymentTag: driver?.repayment_tag ?? '',
            photo: '',
        }),
        [profile?.birth_date, driver?.repayment_tag],
    );

    const personal = useInlineGroup({
        server: personalServer,
        subject: profileId ? `profile:${profileId}` : null,
        save: async (draft) => {
            // TWO TABLES, TWO WRITES, AND ONLY WHERE SOMETHING MOVED. The repayment tag is a
            // `drivers` column and the rest are `profiles`; sending both unconditionally would
            // rewrite a tag nobody touched.
            if (
                draft.birthDate !== personalServer.birthDate ||
                draft.photo !== personalServer.photo
            ) {
                await saveDriverProfile({
                    profileId,
                    patch: { birth_date: draft.birthDate || null },
                    photoFile: photo.file,
                });
            }
            if (draft.repaymentTag !== personalServer.repaymentTag) {
                await saveRepaymentTag({ driverId, repaymentTag: draft.repaymentTag });
            }
        },
        onSaved: invalidate,
    });
    const photo = useStagedFile(personal, 'photo');

    if (isLoading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!driver || !profile) {
        return (
            <Blocks>
                <Block id="b-name" title="Legal name">
                    <p className="sd-lede sd-dim">
                        No driver is assigned to this contract. Every instrument in the journey names
                        one, so nothing here can be filled in until the assignment is made.
                    </p>
                </Block>
            </Blocks>
        );
    }

    const file = driverFileCount(documents, profile, driver);

    return (
        <Blocks>
            <Block id="b-name" title="Legal name">
                <p className="sd-lede sd-dim">
                    Prints twice on the hire purchase agreement and twice on the deposit agreement.
                    The wording freezes at the first signature, not when the document is created — so
                    a correction made now still reaches an unsigned agreement.
                </p>
                <InlineFieldGroup group={name}>
                    <Rows>
                        <QuietField
                            {...name.field('firstName')}
                            id="f-firstName"
                            label="First name"
                            placeholder="Missing"
                            format="prints on 2 instruments"
                        />
                        <QuietField
                            {...name.field('middleName')}
                            id="f-middleName"
                            label="Middle name"
                            placeholder="None"
                        />
                        <QuietField
                            {...name.field('lastName')}
                            id="f-lastName"
                            label="Last name"
                            placeholder="Missing"
                        />
                    </Rows>
                </InlineFieldGroup>
            </Block>

            <Block id="b-contact" title="Contact">
                <p className="sd-lede sd-dim">
                    Phone prints on the hire purchase agreement and on the deposit agreement. Address
                    prints on the hire purchase agreement — it is the one contact detail with a real
                    wait, because most drivers have to go and find their digital address.
                </p>
                <InlineFieldGroup group={contact}>
                    <Rows>
                        <QuietField
                            {...contact.field('phone')}
                            id="f-phone"
                            label="Phone"
                            inputMode="tel"
                            placeholder="Missing"
                            format="prints on 2 instruments"
                        />
                        {/* THE PLACEHOLDER CARRIES THE CONSEQUENCE, not the `format` hint: the
                            format line is invisible until the field has focus, and what an empty
                            address costs is exactly what has to be readable without touching it. */}
                        <QuietField
                            {...contact.field('address')}
                            id="f-address"
                            label="Address"
                            placeholder="Missing — the agreement cannot be sent without it"
                            format="prints on the hire purchase agreement"
                        />
                        <QuietField
                            {...contact.field('email')}
                            id="f-email"
                            label="Email"
                            type="email"
                            placeholder="Not on file · optional"
                        />
                    </Rows>
                </InlineFieldGroup>
            </Block>

            <Block id="b-driverfile" title="Driver file" aside="records, not requirements">
                <p className="sd-lede sd-dim">
                    {file.recorded} of {file.total} recorded. None of these print on an instrument
                    and none gate activation — they are the file you will want the day something goes
                    wrong.
                </p>

                <Rows>
                    {DRIVER_DOCUMENT_TYPES.map(({ type, label }) => (
                        <DocumentGroup
                            key={type}
                            type={type}
                            label={label}
                            documents={documents}
                            driverId={driver.id}
                            contractId={contractId}
                            onSaved={onSuccess}
                        />
                    ))}
                </Rows>

                <div className="mt-6">
                    <InlineFieldGroup
                        group={personal}
                        footnote="The photograph, the date of birth and the repayment tag commit together."
                    >
                        <Rows>
                            <InlineUpload
                                id="f-photo"
                                label="Photograph"
                                current={{ url: profile.photo ?? null }}
                                file={photo.file}
                                onFileChange={photo.stage}
                                accept="image/*"
                                disabled={personal.saving}
                            />
                            <QuietField
                                {...personal.field('birthDate')}
                                id="f-birthDate"
                                label="Date of birth"
                                type="date"
                            />
                            <QuietField
                                {...personal.field('repaymentTag')}
                                id="f-repaymentTag"
                                label="Repayment tag"
                                placeholder="Not on file"
                                format="how this driver's payments are recognised"
                            />
                        </Rows>
                    </InlineFieldGroup>
                </div>

                <Hint className="mt-3">
                    The Ghana Card number is a blank fill-in line on the printed instrument, not a merge
                    field — there is nowhere to type it, and nothing reads it.
                </Hint>
            </Block>
        </Blocks>
    );
}
